import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FormControlLabel, Grid, Switch } from '@mui/material';
import { DynamicField, Typography } from '@totalsoft/rocket-ui';
import { getDynamicFieldControlType } from 'utils/functions';
import {
  CalendarCheckbox,
  CalendarOptionRow,
  CalendarOptionsWarning,
  ColoredCircle,
  NoCalendarOptionsContainer
} from './OfferPaymentPlanStyles';
import { Error } from '@mui/icons-material';

function OfferPaymentPlanParamsCalendarOptions({
  scheduleCellParamColumns,
  selectedScheduleCells,
  getDynamicInputParams,
  handleRowsPropertyChange,
  isEditable,
  selectedOptions,
  setSelectedOptions,
  onResetSelectedCells
}) {
  const { t } = useTranslation();

  const allSelectedCellsAreEqual = scheduleCellParamColumns?.every(prop =>
    selectedScheduleCells?.every(selectedCell => {
      return (
        selectedOptions[selectedCell.Index] &&
        selectedCell[prop.cellParamName] === selectedScheduleCells[0][prop.cellParamName] &&
        selectedOptions[selectedCell.Index][prop.cellParamName] === selectedOptions[selectedScheduleCells[0].Index][prop.cellParamName]
      );
    })
  );

  const handleCheckboxChange = cellParam => event => {
    const { cellParamName, controlType } = cellParam;
    const isChecked = event.target.checked;

    if (!allSelectedCellsAreEqual) onResetSelectedCells(selectedScheduleCells);

    if (!isChecked) handleRowsPropertyChange(selectedScheduleCells, cellParamName, null);
    else if (controlType === 'BOOL') handleRowsPropertyChange(selectedScheduleCells, cellParamName, false);

    setSelectedOptions(prevState => {
      const newSelectedParams = [...prevState];
      selectedScheduleCells.forEach(cell => {
        newSelectedParams[cell.Index] = { ...newSelectedParams[cell.Index], [cellParamName]: isChecked };
      });
      return newSelectedParams;
    });
  };

  const handleOptionValueChange = paramName => value => {
    allSelectedCellsAreEqual && handleRowsPropertyChange(selectedScheduleCells, paramName, value);
  };

  return selectedScheduleCells.length ? (
    <>
      {!allSelectedCellsAreEqual && (
        <CalendarOptionsWarning item container mt={3} px={2} py={1} xs={12} gap={1} alignItems='center'>
          <Error fontSize='small' />
          <Typography>{t('OfferEdit.PaymentPlan.OptionsWarning')}</Typography>
        </CalendarOptionsWarning>
      )}
      <Grid item container xs={12} mt={2} columnSpacing={4} rowSpacing={1}>
        {scheduleCellParamColumns?.map((cellParam, idx) => (
          <Grid key={idx} item container xs={12} xl={6} gap={2} alignItems='center'>
            <Grid item xs='auto' gap={1}>
              <CalendarCheckbox
                checked={Boolean(allSelectedCellsAreEqual && selectedOptions[selectedScheduleCells[0].Index][cellParam.cellParamName])}
                onChange={event => handleCheckboxChange(cellParam)(event)}
                disabled={!isEditable}
              />
            </Grid>
            <Grid item container xs gap={1} alignItems='center'>
              <Grid item container xs='auto'>
                <ColoredCircle colorIndex={idx} />
              </Grid>
              <CalendarOptionRow item container xs px={2} py={1} alignItems='center'>
                <Grid item xs>
                  <Typography emphasis='bold'>{cellParam.cellParamDisplayName}</Typography>
                </Grid>
                <Grid item container xs gap={1} justifyContent='center'>
                  {cellParam.controlType === 'BOOL' ? (
                    <FormControlLabel
                      width='100%'
                      control={
                        <Switch
                          size='small'
                          checked={Boolean(allSelectedCellsAreEqual && selectedScheduleCells[0][cellParam.cellParamName])}
                          onChange={event => handleOptionValueChange(cellParam.cellParamName)(event.target.checked)}
                          disabled={
                            !isEditable ||
                            !allSelectedCellsAreEqual ||
                            !selectedOptions[selectedScheduleCells[0].Index][cellParam.cellParamName]
                          }
                        />
                      }
                    />
                  ) : (
                    <DynamicField
                      id={'offerPaymentPlanParamsCard_' + cellParam.cellParamName}
                      controlType={getDynamicFieldControlType(getDynamicInputParams(cellParam.controlType).controlType)}
                      inputProps={getDynamicInputParams(cellParam.controlType).inputProps}
                      isClearable={false}
                      value={allSelectedCellsAreEqual ? selectedScheduleCells[0][cellParam.cellParamName] : ''}
                      onChange={value => handleOptionValueChange(cellParam.cellParamName)(value)}
                      disabled={
                        !isEditable ||
                        !allSelectedCellsAreEqual ||
                        !selectedOptions[selectedScheduleCells[0].Index][cellParam.cellParamName]
                      }
                    />
                  )}
                </Grid>
              </CalendarOptionRow>
            </Grid>
          </Grid>
        ))}
      </Grid>
    </>
  ) : (
    <NoCalendarOptionsContainer container mt={3} p={2}>
      <Grid item container xs={12} alignItems='center' justifyContent='center'>
        <CalendarCheckbox disabled />
      </Grid>
      <Grid item container xs={12} alignItems='center' justifyContent='center'>
        <Typography color='primary' emphasis='bold' variant='button' fontSize={16}>
          {t('OfferEdit.PaymentPlan.Options')}
        </Typography>
      </Grid>
      <Grid item container mt={1} xs={12} alignItems='center' justifyContent='center'>
        <Typography color='textSecondary' emphasis='bold'>
          {t('OfferEdit.PaymentPlan.PleaseSelectMonth')}
        </Typography>
      </Grid>
    </NoCalendarOptionsContainer>
  );
}

OfferPaymentPlanParamsCalendarOptions.propTypes = {
  scheduleCellParamColumns: PropTypes.array.isRequired,
  selectedScheduleCells: PropTypes.array.isRequired,
  getDynamicInputParams: PropTypes.func.isRequired,
  handleRowsPropertyChange: PropTypes.func.isRequired,
  isEditable: PropTypes.bool.isRequired,
  selectedOptions: PropTypes.array.isRequired,
  setSelectedOptions: PropTypes.func.isRequired,
  onResetSelectedCells: PropTypes.func.isRequired
};

export default OfferPaymentPlanParamsCalendarOptions;
